import React from 'react';
import { X, ShoppingCart, Check, Plus, ArrowRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { resolveImage } from './admin/ImageUpload';

/**
 * "Don't forget these essential items!" — the post-add-to-cart nudge that
 * lists a product's admin-curated accessories with quick add-to-cart buttons.
 *
 * Rendered as a right-hand slide-in sidebar on desktop and a full-screen
 * modal on mobile (below md). Only opens when the parent product actually
 * has ≥1 linked accessory; parent decides whether to fire it.
 */
export const CartNudgeDialog = ({
  open,
  onClose,
  parentProduct,     // the product just added
  accessories = [],  // hydrated cross-sell docs (already filtered)
}) => {
  const { addItem, items: cartItems } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!open || !parentProduct) return null;

  const inCartIds = new Set(cartItems.map(i => i.id));

  const quickAdd = async (p) => {
    if (!user) {
      toast.info('Please sign in to add items to your basket');
      navigate('/login?redirect=' + encodeURIComponent(`/product/${parentProduct.slug || parentProduct.id}`));
      onClose();
      return;
    }
    await addItem(p, 1, null, {});
    toast.success(`${p.name} added to basket`);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-end bg-black/50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="cart-nudge-dialog"
    >
      <div
        className="w-full md:w-[420px] bg-white shadow-2xl overflow-y-auto flex flex-col animate-in slide-in-from-right"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-neutral-200 flex items-start gap-3 bg-[#12263A] text-white sticky top-0 z-10">
          <div className="bg-[#F46A25] p-2 rounded-sm">
            <Check className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-black text-lg leading-tight">Added to basket</div>
            <div className="text-xs text-neutral-300 mt-0.5 truncate">{parentProduct.name}</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="cart-nudge-close"
            className="text-neutral-300 hover:text-white p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nudge body */}
        <div className="p-4 flex-1">
          <div className="mb-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#F46A25]">Don't forget</div>
            <h3 className="text-lg font-black text-[#12263A]">Essential items to complete your project</h3>
            <p className="text-xs text-neutral-600 mt-1">Recommended by our team to pair with {parentProduct.brand} {parentProduct.name}.</p>
          </div>

          <div className="space-y-2">
            {accessories.map(p => {
              const inCart = inCartIds.has(p.id);
              return (
                <div
                  key={p.id}
                  data-testid={`cart-nudge-item-${p.id}`}
                  className="flex items-center gap-3 border border-neutral-200 hover:border-[#F46A25] rounded-sm p-2 transition-colors"
                >
                  <Link to={`/product/${p.slug || p.id}`} onClick={onClose} className="flex-shrink-0">
                    <img src={resolveImage(p.image)} alt={p.name} className="w-14 h-14 object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/product/${p.slug || p.id}`}
                      onClick={onClose}
                      className="block text-sm font-semibold text-[#12263A] hover:text-[#F46A25] leading-tight line-clamp-2"
                    >
                      {p.name}
                    </Link>
                    <div className="text-[11px] text-neutral-500">{p.brand}</div>
                    <div className="text-[#F46A25] font-bold text-sm mt-0.5">£{Number(p.price).toFixed(2)}</div>
                  </div>
                  <button
                    onClick={() => quickAdd(p)}
                    disabled={inCart}
                    data-testid={`cart-nudge-add-${p.id}`}
                    className={`flex-shrink-0 text-xs font-bold py-2 px-3 rounded-sm flex items-center gap-1 ${
                      inCart
                        ? 'bg-green-50 text-green-700 border border-green-300 cursor-default'
                        : 'bg-[#F46A25] hover:bg-[#d95a1c] text-white'
                    }`}
                    title={inCart ? 'Already in your basket' : 'Add to basket'}
                  >
                    {inCart ? <><Check className="w-3 h-3" /> Added</> : <><Plus className="w-3 h-3" /> Add</>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer CTAs */}
        <div className="p-4 border-t border-neutral-200 sticky bottom-0 bg-white flex gap-2">
          <button
            onClick={onClose}
            data-testid="cart-nudge-continue"
            className="flex-1 border border-neutral-300 hover:border-[#F46A25] hover:text-[#F46A25] text-[#12263A] text-sm font-semibold py-2.5 rounded-sm"
          >
            Continue shopping
          </button>
          <Link
            to="/cart"
            onClick={onClose}
            data-testid="cart-nudge-view-basket"
            className="flex-1 bg-[#F46A25] hover:bg-[#d95a1c] text-white text-sm font-bold py-2.5 rounded-sm flex items-center justify-center gap-1"
          >
            <ShoppingCart className="w-4 h-4" /> View basket <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CartNudgeDialog;
